import type { ShubhMuhuratResponse } from "@/lib/api/muhurat";

export interface MuhuratVerdict {
  tone: "good" | "wait" | "neutral";
  heading: string;
  detail: string;
}

interface TimeWindow {
  name: string;
  start: string;
  end: string;
}

export type MuhuratVerdictLocale = "en" | "hi";

const VERDICT_COPY = {
  en: {
    waitHeading: "Wait before starting something important",
    waitDetail: (name: string, end: string, place: string) =>
      `${name} is active until ${end} in ${place}.`,
    goodHeading: "A shubh window is active now",
    goodDetail: (name: string, end: string, place: string) =>
      `${name} remains active until ${end} in ${place}.`,
    previousNightDetail: (name: string, end: string, place: string) =>
      `${name} from last night's cycle remains active until ${end} in ${place}.`,
    neutralHeading: "No primary shubh window is active now",
    nextDetail: (name: string, start: string, end: string, place: string) =>
      `The next listed window is ${name}, from ${start} to ${end} in ${place}.`,
    endedDetail: (place: string) =>
      `Today's listed shubh windows in ${place} have ended. Check again after the date changes.`,
  },
  hi: {
    waitHeading: "कोई महत्वपूर्ण काम शुरू करने से पहले रुकें",
    waitDetail: (name: string, end: string, place: string) =>
      `${place} में ${name} ${end} तक चल रहा है।`,
    goodHeading: "अभी शुभ समय चल रहा है",
    goodDetail: (name: string, end: string, place: string) =>
      `${place} में ${name} ${end} तक रहेगा।`,
    previousNightDetail: (name: string, end: string, place: string) =>
      `${place} में पिछली रात के चक्र का ${name} ${end} तक रहेगा।`,
    neutralHeading: "अभी कोई प्रमुख शुभ समय नहीं चल रहा है",
    nextDetail: (name: string, start: string, end: string, place: string) =>
      `${place} में अगला शुभ समय ${name} है — ${start} से ${end} तक।`,
    endedDetail: (place: string) =>
      `${place} में आज के सभी सूचीबद्ध शुभ समय बीत चुके हैं। तारीख बदलने के बाद फिर देखें।`,
  },
} as const;

type NamedWindow = { name_en: string; name_hi: string; start: string; end: string };

function toTimeWindow(window: NamedWindow, locale: MuhuratVerdictLocale): TimeWindow {
  return {
    name: locale === "hi" ? window.name_hi : window.name_en,
    start: window.start,
    end: window.end,
  };
}

function clockMinute(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

function containsMinute(now: number, window: TimeWindow): boolean {
  const start = clockMinute(window.start);
  const end = clockMinute(window.end);
  if (start === null || end === null) return false;
  if (start <= end) return now >= start && now < end;

  // A digest is calculated for its requested civil date. A wrapped window
  // starts that evening and ends on the following date, so its post-midnight
  // portion must not be applied to the beginning of the requested date.
  return now >= start;
}

function containsPreviousNightMinute(now: number, window: TimeWindow): boolean {
  const start = clockMinute(window.start);
  const end = clockMinute(window.end);
  return start !== null && end !== null && start > end && now < end;
}

function nextWindow(now: number, windows: TimeWindow[]): TimeWindow | null {
  return (
    windows
      .map((window) => ({ window, start: clockMinute(window.start) }))
      .filter((item): item is { window: TimeWindow; start: number } => item.start !== null)
      .filter((item) => item.start > now)
      .sort((left, right) => left.start - right.start)[0]?.window ?? null
  );
}

function auspiciousWindows(
  digest: ShubhMuhuratResponse,
  locale: MuhuratVerdictLocale
): TimeWindow[] {
  // Skew-tolerant: an older backend / cached response may not serve
  // `recommended_windows` yet — degrade to no windows, never crash prerender.
  return (digest.recommended_windows ?? []).map((window) => toTimeWindow(window, locale));
}

export function currentIndiaMinute(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return Number(values.hour) * 60 + Number(values.minute);
}

export function previousCivilDate(dateValue: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (!match) throw new Error(`Invalid civil date: ${dateValue}`);
  const value = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  value.setUTCDate(value.getUTCDate() - 1);
  return value.toISOString().slice(0, 10);
}

export function needsPreviousNightDigest(digest: ShubhMuhuratResponse, nowMinute: number): boolean {
  const sunrise = clockMinute(digest.sunrise);
  return sunrise !== null && nowMinute < sunrise;
}

export function getCurrentMuhuratVerdict(
  digest: ShubhMuhuratResponse,
  nowMinute: number,
  previousNightDigest: ShubhMuhuratResponse | null = null,
  locale: MuhuratVerdictLocale = "en"
): MuhuratVerdict {
  const copy = VERDICT_COPY[locale];
  const avoid = digest.inauspicious
    .map((window) => toTimeWindow(window, locale))
    .find((window) => containsMinute(nowMinute, window));
  if (avoid) {
    return {
      tone: "wait",
      heading: copy.waitHeading,
      detail: copy.waitDetail(avoid.name, avoid.end, digest.place),
    };
  }

  const windows = auspiciousWindows(digest, locale);
  const active = windows.find((window) => containsMinute(nowMinute, window));
  if (active) {
    return {
      tone: "good",
      heading: copy.goodHeading,
      detail: copy.goodDetail(active.name, active.end, digest.place),
    };
  }

  const previousNight = previousNightDigest?.recommended_windows
    ?.filter((window) => window.kind === "choghadiya")
    .map((window) => toTimeWindow(window, locale))
    .find((window) => containsPreviousNightMinute(nowMinute, window));
  if (needsPreviousNightDigest(digest, nowMinute) && previousNight) {
    return {
      tone: "good",
      heading: copy.goodHeading,
      detail: copy.previousNightDetail(previousNight.name, previousNight.end, digest.place),
    };
  }

  const next = nextWindow(nowMinute, windows);
  return {
    tone: "neutral",
    heading: copy.neutralHeading,
    detail: next
      ? copy.nextDetail(next.name, next.start, next.end, digest.place)
      : copy.endedDetail(digest.place),
  };
}
